import { Button, Grid, TextField, Typography, FormLabel } from "@mui/material";
import { FastField, Form, Formik } from "formik";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import Loader from "../../../components/Loader";
import { getCurrentUrl } from "../../../utils/urls";
import { CLIENTS_URL } from "../constants";
import TestsDropdown from "./TestsDropdown";
import ClientCheckboxes from "./ClientCheckboxes";
import { MODULES } from "./constants";
import { toast } from "react-toastify";
import React from "react";
import { FUTUREBILITY_COCKPIT_URL } from "../../Frontend/constants";

function InnerForm({
  client,
  error,
  loading,
  editMode,
  values,
  setFieldValue,
}) {
  const { t } = useTranslation();
  const history = useHistory();

  function copyToClipboard(e) {
    navigator.clipboard.writeText(e.target.value).then(() => {
      toast.info(t("tool.client.copiedToClipboard", "Copied to Clipboard"));
    });
  }

  return (
    <Grid container spacing={4}>
      <Grid item xs={7}>
        <FastField
          name="name"
          as={TextField}
          required
          label={t("tool.clientForm.name", "Name")}
          fullWidth
          variant="outlined"
        />
      </Grid>
      <Grid item xs={7}>
        <FastField
          name="favIconUrl"
          as={TextField}
          label={t("tool.clientForm.favIcon", "Fav Icon URL")}
          fullWidth
          variant="outlined"
        />
      </Grid>
      <Grid item xs={7}>
        <FastField
          name="logoUrl"
          as={TextField}
          label={t("tool.clientForm.logoUrl", "Logo URL")}
          fullWidth
          variant="outlined"
        />
      </Grid>
      <Grid item xs={7}>
        <FastField
          name="languageSelectionHtml"
          as={TextField}
          label={t(
            "tool.clientForm.languageSelectionHtml",
            "Language selection HTML"
          )}
          multiline
          rows={4}
          variant="outlined"
          fullWidth
        />
      </Grid>
      <Grid item xs={7}>
        <Grid item xs={4}>
          <FastField
            name="minimumParticipantsForItems"
            as={TextField}
            label={t(
              "tool.clientForm.minimumParticipantsForItems",
              "Minimum Participants for Items"
            )}
            fullWidth
            variant="outlined"
            type="number"
            size="small"
            inputProps={{
              min: 1,
            }}
          />
        </Grid>
      </Grid>
      <Grid item xs={7}>
        <ClientCheckboxes />
      </Grid>
      <Grid item xs={12}>
        {client && (
          <>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <FormLabel>{t("tool.clientForm.cockpit", "Cockpit")}</FormLabel>
                <TextField
                  variant="outlined"
                  fullWidth
                  defaultValue={getCurrentUrl(
                    `${FUTUREBILITY_COCKPIT_URL}/${
                      client._id
                    }/start?key=${window.btoa(client._id)}`
                  )}
                  size="small"
                  placeholder="Cockpit url"
                  onClick={copyToClipboard}
                  sx={{ margin: "0 0 24px" }}
                  InputProps={{
                    readOnly: true,
                  }}
                />
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <FormLabel>
                  {t("tool.clientForm.participationUrl", "Participation URL")}
                </FormLabel>
                <TextField
                  variant="outlined"
                  fullWidth
                  defaultValue={getCurrentUrl(`/start/${client._id}/`)}
                  size="small"
                  placeholder="Participation url"
                  onClick={copyToClipboard}
                  sx={{ margin: "0 0 24px" }}
                  InputProps={{
                    readOnly: true,
                  }}
                />
              </Grid>
            </Grid>
            <Grid item xs={4}>
              <TestsDropdown
                testIds={values.testIds}
                setFieldValue={setFieldValue}
              />
            </Grid>
          </>
        )}
        <Grid container spacing={2}>
          <Grid item xs={3}>
            <Button
              color="primary"
              variant="contained"
              type="submit"
              disabled={loading}
            >
              {editMode
                ? t("tool.addEdit.save", "Save")
                : t("tool.addEdit.create", "Create")}
            </Button>
            <Button
              color="secondary"
              onClick={() => {
                history.push(CLIENTS_URL);
              }}
            >
              {t("tool.reportForm.close", "Close")}
            </Button>
          </Grid>
        </Grid>
        {error && (
          <Typography color="secondary">
            {error?.response?.data?.message ??
              t("tool.addEdit.unexpectedError", "Unexpected error occurred")}
          </Typography>
        )}
        {loading && <Loader />}
      </Grid>
    </Grid>
  );
}

function ClientForm({ client, onSubmit, error, loading, editMode }) {
  const history = useHistory();

  function extractClientFields(client) {
    return Object.keys(client).reduce((acc, key) => {
      return { ...acc, [key]: client[key] };
    }, {});
  }

  return (
    <Formik
      initialValues={{
        name: "",
        modules: [...MODULES],
        testIds: [],
        minimumParticipantsForItems: 1,
        ...(client && extractClientFields(client)),
      }}
      onSubmit={async values => {
        const res = await onSubmit({
          ...values,
          testIds: values.testIds.filter(Boolean),
        });

        if (res) {
          history.push(`${CLIENTS_URL}/${res._id}`);
        }
      }}
    >
      {({ values, setFieldValue }) => {
        return (
          <Form>
            <InnerForm
              client={client}
              error={error}
              loading={loading}
              editMode={editMode}
              values={values}
              setFieldValue={setFieldValue}
            />
          </Form>
        );
      }}
    </Formik>
  );
}

export default ClientForm;
